/**
 * Upload size constants — single source of truth for file upload limits.
 *
 * Mirrored backend-side in `functions/src/lib/uploadLimits.ts`. When changing
 * values here, update the backend mirror and `storage.rules` in the same pass.
 *
 * Raised to 25MB/100MB on 2026-05-06 (was 10MB per file).
 * Raised to 50MB/250MB on 2026-06-06 to accommodate large scanned fax batches.
 * Per-file raised to 300MB on 2026-09-14.
 * 1GB per file / 50GB per batch on 2026-09-14 (Malaika: "1GB per file, 50 GB in
 * total", fifty files at a time).
 *
 * ── 50GB PER FILE, FIFTY FILES PER MESSAGE (2026-10-08, Miles: "allowed for
 *    all file sizes up to 50 GB for each and any file … 50 GB files up to 50
 *    files total … really really reliable and robust") ──
 *
 * The raise came with a report: an 11-minute recording reached its recipient as
 * one minute. What a person attaches arrives whole, at any size up to this
 * limit, or the send says it did not. The per-file limit is a STORAGE AND
 * TRANSFER promise: the bytes travel by the resumable lane
 * (attachmentStorage.ts: a stall watchdog, never a wall-clock race) and the
 * stored object's length is checked against the picked file before the record
 * counts as delivered. READING a file is bounded separately and stated to the
 * person when it applies: media is transcribed up to MAX_INLINE_BYTES_PER_FILE,
 * the document reader has its own caps, and indexing covers what its
 * continuation budget reaches (ingestJobs.ts MAX_SLICE_CONTINUATIONS). A file
 * past a reading cap is still stored, shared and downloadable in full.
 *
 * THESE FOUR CONSTANTS ARE PRODUCT LIMITS AND NOTHING ELSE. That distinction
 * is the whole point of this revision. Every previous raise moved one number
 * that was simultaneously a product promise, a memory budget and a request-body
 * ceiling, so raising it for the lane that could take it also raised it for two
 * lanes that could not. The memory-bound numbers now live beside them, under
 * their own names, and a raise here does not move them.
 *
 * PER-FILE (50GB) is bounded by TIME, not memory. No lane that serves it holds
 * the file: the browser streams it from disk a chunk at a time, extraction
 * COPIES a large text file server-side inside GCS and reads a bounded head
 * (services/doc-ingest/streamtext.py), and indexing reads it back from storage
 * a window at a time, so peak memory is a property of the window. What one
 * invocation can finish is 2.71 s/MB (measured live 2026-09-14: 45 MB in
 * 121.9 s) against prest0nProcessIngestJob's 540 s — the platform maximum for
 * an event-driven trigger, and unraisable. The slice loop pauses at its budget
 * with a checkpoint and a fresh invocation resumes from it, up to
 * MAX_SLICE_CONTINUATIONS; a document longer than that is indexed as far as
 * the budget reaches and the coverage is recorded, never presented as whole.
 *
 * TOTAL (fifty files at the per-file limit) is bounded by nothing in one
 * process, because no lane holds file bytes: GCS paths go to the extractor,
 * byte ranges come back for indexing, and the fifty files run in parallel as
 * fifty independent jobs. It is the batch size the product promises, and it is
 * NOT "per-file times fifty" by coincidence — it is the same fifty files.
 *
 * THE INLINE LIMITS BELOW ARE THE ONES THAT GUARD MEMORY, and they have not
 * moved. Read them before touching anything here.
 */

/** Maximum raw bytes per uploaded file (50 GB). Product limit; see above. */
export const MAX_UPLOAD_BYTES_PER_FILE = 50 * 1024 * 1024 * 1024;

/** Maximum aggregate raw bytes across all attachments in one batch (fifty files at the per-file limit, 2.5 TB). */
export const MAX_UPLOAD_BYTES_TOTAL = 50 * 50 * 1024 * 1024 * 1024;

/**
 * ── THE MEMORY-BOUND LIMITS ──────────────────────────────────────────────
 *
 * Bytes a single invocation may hold. Everything above is about what the
 * PLATFORM accepts; these two are about what one 2 GiB instance survives, and
 * they are the numbers the product limits used to be silently doubling as.
 *
 * They bound two lanes:
 *
 *   * attachmentDocumentAnalysis, which downloads every attachment into
 *     `prepared[]` BEFORE analysing any of them — "a guard on a memory shape,
 *     wearing the costume of a product limit", in its own words. It runs only
 *     when the ingest lane produced nothing, and on salvage of files that lane
 *     could not read, so a file past this limit is declined there with a
 *     stated reason and read by the lane that can read it.
 *   * the widget's inline base64 path (maestroWidgetChat), where the bytes
 *     are the REQUEST BODY. That surface is public and account-less; 50 GB
 *     times the base64 overhead is not a ceiling anyone should be able to
 *     aim at it.
 *
 * Their values are exactly what the product limits held before 2026-09-14, so
 * both lanes behave byte for byte as they did. Raising either needs the lane
 * changed first, not a bigger number here.
 */
export const MAX_INLINE_BYTES_PER_FILE = 300 * 1024 * 1024;
export const MAX_INLINE_BYTES_TOTAL = 250 * 1024 * 1024;

/** Human-readable label used in UI messages (no trailing period). */
export const MAX_UPLOAD_LABEL_PER_FILE = '50GB';
export const MAX_UPLOAD_LABEL_TOTAL = '2.5TB';
export const MAX_INLINE_LABEL_PER_FILE = '300MB';
export const MAX_INLINE_LABEL_TOTAL = '250MB';
